/**
 * VerlofPage — Leave request approval dashboard (admin only)
 * Shows pending requests with approve/reject, team calendar, recent decisions.
 *
 * Elke openstaande kaart is een beslispunt: saldo vóór en na goedkeuring
 * (lagen met vervaldatum), overlap met eigen aanvragen, collega's die al vrij
 * zijn, rooster/rit en feestdagen komen uit `GET ?action=beoordeling`. Een
 * blokkerende waarschuwing schakelt Goedkeuren uit. Goedgekeurd verlof dat
 * nog niet voorbij is kan kantoor annuleren met een verplichte reden.
 */

import { useEffect, useState, useMemo } from 'react';
import { format, parseISO, startOfWeek, addDays, getWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card } from '../components/ui';
import { useWorkforceStore } from '../stores';
import type { LeaveRequest } from '../stores/workforce-store';
import { getLeaveLabel, getLeaveColor, LEAVE_STATUS_LABELS, getLeaveStatusLabel, type LeaveRequestStatus } from '../lib/leave-types';
import { ERNST_CHIP, type Beoordeling } from '../lib/verlof-beoordeling-types';
import type { VakantieSaldo } from '../lib/vakantie-saldo';
import { LeaveOnBehalfDialog } from '../components/personeel/LeaveOnBehalfDialog';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
const AFGEWEZEN_SET = new Set<LeaveRequestStatus>(['rejected', 'withdrawn', 'cancelled']);

function uren(n: number | null | undefined): string {
  if (n === null || n === undefined) return '–';
  return `${(Math.round(n * 100) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 2 })}u`;
}

function datumKort(iso: string): string {
  return format(parseISO(iso), 'd MMM yyyy', { locale: nl });
}

function vervaltTekst(saldo: VakantieSaldo | null): string | null {
  if (!saldo?.vervalt_eerst) return null;
  const v = saldo.vervalt_eerst;
  return `${v.laag} ${v.jaar}: ${uren(v.uren)} vervalt ${datumKort(v.vervalt_op)}`;
}

/** Compacte beoordelingsregel onder de datumregel van een openstaande kaart. */
function BeoordelingRegel({ beoordeling, laden }: { beoordeling: Beoordeling | undefined; laden: boolean }) {
  if (!beoordeling) {
    return <p className="text-xs text-slate-400 mt-1.5">{laden ? 'Beoordeling laden…' : 'Beoordeling niet beschikbaar'}</p>;
  }
  const voor = beoordeling.saldo_voor;
  const na = beoordeling.saldo_na;
  const vervalt = vervaltTekst(na ?? voor);
  return (
    <div className="mt-1.5 space-y-1.5">
      {voor && na && (
        <p className="text-xs text-slate-600">
          Saldo {uren(voor.beschikbaar)} → <span className={na.beschikbaar < 0 ? 'text-red-600 font-medium' : 'font-medium'}>{uren(na.beschikbaar)}</span>
          {vervalt && <span className="text-slate-400"> ({vervalt})</span>}
        </p>
      )}
      {beoordeling.waarschuwingen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {beoordeling.waarschuwingen.map((w, i) => (
            <span
              key={`${w.code}-${i}`}
              title={w.tekst}
              className={`inline-flex items-center text-[11px] leading-4 px-2 py-0.5 rounded-full border ${ERNST_CHIP[w.ernst]}`}
            >
              {w.tekst}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function VerlofPage() {
  const {
    leaveRequests,
    staffList,
    leaveEntries,
    fetchLeaveRequests,
    fetchStaff,
    fetchLeave,
    approveLeaveRequest,
    rejectLeaveRequest,
    cancelLeaveRequest,
    fetchVerlofBeoordeling,
    verlofBeoordelingen,
  } = useWorkforceStore();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [rejectDialogId, setRejectDialogId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelDialog, setCancelDialog] = useState<LeaveRequest | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showOnBehalfDialog, setShowOnBehalfDialog] = useState(false);
  const [beoordelingLaden, setBeoordelingLaden] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchLeaveRequests();
    fetchStaff();
    // Fetch leave entries for the team calendar (current month range)
    const now = new Date();
    const from = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const future = new Date(now);
    future.setDate(future.getDate() + 60);
    fetchLeave({ from, to: format(future, 'yyyy-MM-dd') });
  }, [fetchLeaveRequests, fetchStaff, fetchLeave]);

  const pending = useMemo(() =>
    leaveRequests.filter(r => r.status === 'pending'),
    [leaveRequests]
  );

  // Beoordeling per openstaande kaart (store-cache; invalideert na een besluit)
  useEffect(() => {
    const ontbreekt = pending.filter(r => !verlofBeoordelingen[r.id] && !beoordelingLaden.has(r.id));
    if (ontbreekt.length === 0) return;
    setBeoordelingLaden(prev => {
      const next = new Set(prev);
      ontbreekt.forEach(r => next.add(r.id));
      return next;
    });
    Promise.all(ontbreekt.map(r => fetchVerlofBeoordeling(r.id))).finally(() => {
      setBeoordelingLaden(prev => {
        const next = new Set(prev);
        ontbreekt.forEach(r => next.delete(r.id));
        return next;
      });
    });
  }, [pending, verlofBeoordelingen, beoordelingLaden, fetchVerlofBeoordeling]);

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return leaveRequests;
    if (statusFilter === 'rejected') return leaveRequests.filter(r => AFGEWEZEN_SET.has(r.status));
    return leaveRequests.filter(r => r.status === statusFilter);
  }, [leaveRequests, statusFilter]);

  const recentDecisions = useMemo(() =>
    leaveRequests
      .filter(r => r.status !== 'pending')
      .slice(0, 10),
    [leaveRequests]
  );

  const vandaag = format(new Date(), 'yyyy-MM-dd');

  // Team calendar
  const calendarWeekStart = useMemo(() => {
    const base = startOfWeek(new Date(), { weekStartsOn: 1 });
    return addDays(base, weekOffset * 7);
  }, [weekOffset]);

  const calendarDates = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => format(addDays(calendarWeekStart, i), 'yyyy-MM-dd')),
    [calendarWeekStart]
  );

  const calendarWeekLabel = useMemo(() => {
    const weekNum = getWeek(calendarWeekStart, { weekStartsOn: 1 });
    return `Week ${weekNum} · ${format(calendarWeekStart, 'd MMM', { locale: nl })} t/m ${format(addDays(calendarWeekStart, 5), 'd MMM yyyy', { locale: nl })}`;
  }, [calendarWeekStart]);

  // Build calendar data: which staff have leave on which days
  const calendarData = useMemo(() => {
    const staffWithLeave = new Map<string, { name: string; days: Map<string, string> }>();

    // From approved leave entries
    for (const entry of leaveEntries) {
      if (!calendarDates.includes(entry.date)) continue;
      const staffName = entry.staff?.name || staffList.find(s => s.id === entry.staff_id)?.name || '?';
      if (!staffWithLeave.has(entry.staff_id)) {
        staffWithLeave.set(entry.staff_id, { name: staffName, days: new Map() });
      }
      staffWithLeave.get(entry.staff_id)!.days.set(entry.date, entry.leave_type);
    }

    // From pending requests (show as planned)
    for (const req of pending) {
      const current = new Date(req.start_date + 'T00:00:00');
      const endDate = new Date(req.end_date + 'T00:00:00');
      while (current <= endDate) {
        const dateStr = format(current, 'yyyy-MM-dd');
        if (calendarDates.includes(dateStr) && current.getDay() !== 0 && current.getDay() !== 6) {
          const staffName = req.staff?.name || staffList.find(s => s.id === req.staff_id)?.name || '?';
          if (!staffWithLeave.has(req.staff_id)) {
            staffWithLeave.set(req.staff_id, { name: staffName, days: new Map() });
          }
          if (!staffWithLeave.get(req.staff_id)!.days.has(dateStr)) {
            staffWithLeave.get(req.staff_id)!.days.set(dateStr, req.leave_type + '_pending');
          }
        }
        current.setDate(current.getDate() + 1);
      }
    }

    return Array.from(staffWithLeave.entries())
      .map(([id, data]) => ({ staffId: id, ...data }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [leaveEntries, pending, calendarDates, staffList]);

  const handleApprove = async (id: string) => {
    setIsProcessing(id);
    setActionError(null);
    try {
      await approveLeaveRequest(id);
    } catch (e) {
      setActionError(`Goedkeuren mislukt: ${e instanceof Error ? e.message : 'onbekende fout'}`);
    }
    setIsProcessing(null);
  };

  const handleReject = async () => {
    if (!rejectDialogId || !rejectReason.trim()) return;
    setIsProcessing(rejectDialogId);
    setActionError(null);
    try {
      await rejectLeaveRequest(rejectDialogId, rejectReason.trim());
      setRejectDialogId(null);
      setRejectReason('');
    } catch (e) {
      setActionError(`Afwijzen mislukt: ${e instanceof Error ? e.message : 'onbekende fout'}`);
    }
    setIsProcessing(null);
  };

  const handleCancel = async () => {
    if (!cancelDialog || !cancelReason.trim()) return;
    setIsProcessing(cancelDialog.id);
    setActionError(null);
    try {
      await cancelLeaveRequest(cancelDialog.id, cancelReason.trim());
      setCancelDialog(null);
      setCancelReason('');
      // Kalender verversen: de entries zijn weg
      const now = new Date();
      const from = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const future = new Date(now);
      future.setDate(future.getDate() + 60);
      fetchLeave({ from, to: format(future, 'yyyy-MM-dd') });
    } catch (e) {
      setActionError(`Annuleren mislukt: ${e instanceof Error ? e.message : 'onbekende fout'}`);
    }
    setIsProcessing(null);
  };

  const beslissingTekst = (req: LeaveRequest): string => {
    const label = getLeaveStatusLabel(req.status);
    if (req.status === 'cancelled') return `${label}${req.cancelled_by ? ` door ${req.cancelled_by}` : ''}`;
    if (req.status === 'withdrawn') return `${label} door de medewerker`;
    return `${label}${req.reviewed_by ? ` door ${req.reviewed_by}` : ''}`;
  };

  const toonBeslissingen = statusFilter === 'all' || statusFilter === 'approved' || statusFilter === 'rejected';

  return (
    <>
      <div className="p-6 max-w-6xl mx-auto overflow-y-auto h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-main">Verlof</h1>
            <p className="text-muted text-sm">
              {pending.length > 0 ? `${pending.length} openstaande aanvra${pending.length === 1 ? 'ag' : 'gen'}` : 'Geen openstaande aanvragen'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
              {([
                { key: 'all', label: 'Alle' },
                { key: 'pending', label: 'Openstaand' },
                { key: 'approved', label: 'Goedgekeurd' },
                { key: 'rejected', label: 'Afgewezen & ingetrokken' },
              ] as { key: StatusFilter; label: string }[]).map(f => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    statusFilter === f.key ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowOnBehalfDialog(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              + Verlof toevoegen
            </button>
          </div>
        </div>

        {actionError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {actionError}
          </div>
        )}

        {/* Pending requests */}
        {pending.length > 0 && statusFilter !== 'approved' && statusFilter !== 'rejected' && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Openstaande aanvragen ({pending.length})
            </h2>
            <div className="space-y-3">
              {pending.map(req => {
                const processing = isProcessing === req.id;
                const beoordeling = verlofBeoordelingen[req.id];
                const geblokkeerd = beoordeling?.geblokkeerd === true;
                const blokkeerTekst = beoordeling?.waarschuwingen.filter(w => w.ernst === 'blokkeert').map(w => w.tekst).join(' ') || '';
                const dagen = beoordeling?.aanvraag.dagen ?? req.days;
                const totaal = beoordeling?.aanvraag.uren ?? req.total_hours;
                return (
                  <Card key={req.id} variant="elevated" padding="none">
                    <div className="p-4 flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-main">{req.staff?.name || '–'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getLeaveColor(req.leave_type)}`}>
                            {getLeaveLabel(req.leave_type)}
                          </span>
                          <span className="text-[11px] text-slate-400">
                            aangevraagd {format(parseISO(req.created_at), 'd MMM', { locale: nl })}
                          </span>
                        </div>
                        <p className="text-sm text-muted">
                          {format(parseISO(req.start_date), 'd MMM', { locale: nl })}
                          {req.start_date !== req.end_date && ` t/m ${format(parseISO(req.end_date), 'd MMM', { locale: nl })}`}
                          <span className="text-slate-400 ml-1">
                            ({dagen} {dagen === 1 ? 'dag' : 'dagen'}{totaal != null ? `, ${uren(totaal)}` : ''})
                          </span>
                        </p>
                        {req.notes && (
                          <p className="text-xs text-slate-500 mt-1 italic">"{req.notes}"</p>
                        )}
                        <BeoordelingRegel beoordeling={beoordeling} laden={beoordelingLaden.has(req.id)} />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleApprove(req.id)}
                          disabled={processing || geblokkeerd}
                          title={geblokkeerd ? blokkeerTekst : undefined}
                          className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {processing ? '...' : 'Goedkeuren'}
                        </button>
                        <button
                          onClick={() => { setRejectDialogId(req.id); setRejectReason(''); }}
                          disabled={processing}
                          className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition-colors"
                        >
                          Afwijzen
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Team calendar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
              Team verlofkalender
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <span className="text-xs font-medium text-main min-w-[240px] text-center">{calendarWeekLabel}</span>
              <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
              {weekOffset !== 0 && (
                <button onClick={() => setWeekOffset(0)} className="px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                  Deze week
                </button>
              )}
            </div>
          </div>
          <Card variant="elevated" padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2 w-40">Medewerker</th>
                    {calendarDates.map(date => (
                      <th key={date} className="text-center text-xs font-medium text-slate-500 px-2 py-2 w-20">
                        {format(parseISO(date), 'EEE d', { locale: nl })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calendarData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-xs text-slate-400 py-6">Geen verlof deze week</td>
                    </tr>
                  ) : (
                    calendarData.map(row => (
                      <tr key={row.staffId} className="border-b border-slate-50">
                        <td className="text-sm text-main px-4 py-2 font-medium">{row.name}</td>
                        {calendarDates.map(date => {
                          const type = row.days.get(date);
                          if (!type) return <td key={date} className="text-center px-2 py-2"><span className="text-slate-200">·</span></td>;
                          const isPending = type.endsWith('_pending');
                          const baseType = isPending ? type.replace('_pending', '') : type;
                          return (
                            <td key={date} className="text-center px-2 py-2">
                              <span className={`inline-block w-6 h-6 rounded ${isPending ? 'bg-amber-100 border border-amber-300 border-dashed' : (getLeaveColor(baseType).split(' ')[0] || 'bg-gray-100')}`} title={`${getLeaveLabel(baseType)}${isPending ? ' (openstaand)' : ''}`} />
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Recent decisions */}
        {recentDecisions.length > 0 && toonBeslissingen && (
          <div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Recente beslissingen
            </h2>
            <Card variant="elevated" padding="none">
              <div className="divide-y divide-slate-50">
                {(statusFilter === 'all' ? recentDecisions : filteredRequests.filter(r => r.status !== 'pending')).map(req => {
                  const st = LEAVE_STATUS_LABELS[req.status as LeaveRequestStatus];
                  const annuleerbaar = req.status === 'approved' && req.end_date >= vandaag;
                  const reden = req.status === 'cancelled' ? req.cancel_reason : req.rejection_reason;
                  return (
                    <div key={req.id} className="px-4 py-3 flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${st?.color || 'bg-gray-100 text-gray-600'}`} title={st?.label}>
                        {st?.mark || '?'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-main">{req.staff?.name || '–'}</span>
                        <span className="text-sm text-muted ml-2">
                          {getLeaveLabel(req.leave_type)}
                        </span>
                        <span className="text-sm text-slate-400 ml-2">
                          {format(parseISO(req.start_date), 'd MMM', { locale: nl })}
                          {req.start_date !== req.end_date && ` t/m ${format(parseISO(req.end_date), 'd MMM', { locale: nl })}`}
                          {req.total_hours != null && <span className="ml-1">({uren(req.total_hours)})</span>}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400">
                        {beslissingTekst(req)}
                      </div>
                      {reden && (
                        <span className="text-xs text-red-500 italic" title={reden}>
                          "{reden.substring(0, 30)}{reden.length > 30 ? '...' : ''}"
                        </span>
                      )}
                      {annuleerbaar && (
                        <button
                          onClick={() => { setCancelDialog(req); setCancelReason(''); }}
                          disabled={isProcessing === req.id}
                          className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors"
                          title="Goedgekeurd verlof annuleren; de medewerker krijgt een mail met de reden"
                        >
                          Annuleren
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Verlof namens medewerker */}
      {showOnBehalfDialog && (
        <LeaveOnBehalfDialog
          staffList={staffList}
          onClose={() => setShowOnBehalfDialog(false)}
          onCreated={() => {
            // Kalender + lijst verversen
            const now = new Date();
            const from = format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd');
            const future = new Date(now);
            future.setDate(future.getDate() + 60);
            fetchLeave({ from, to: format(future, 'yyyy-MM-dd') });
          }}
        />
      )}

      {/* Reject dialog */}
      {rejectDialogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setRejectDialogId(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-main mb-4">Verlofaanvraag afwijzen</h3>
            <p className="text-sm text-muted mb-3">
              Geef een reden voor de afwijzing. De medewerker ontvangt een e-mail.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Bijv. team onderbezet, overlappend met andere aanvraag"
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
              autoFocus
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setRejectDialogId(null)}
                className="flex-1 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Terug
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || isProcessing === rejectDialogId}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {isProcessing === rejectDialogId ? 'Bezig...' : 'Afwijzen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel dialog: goedgekeurd verlof annuleren */}
      {cancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setCancelDialog(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-main mb-1">Goedgekeurd verlof annuleren</h3>
            <p className="text-sm text-muted mb-3">
              {cancelDialog.staff?.name || 'Medewerker'}, {getLeaveLabel(cancelDialog.leave_type).toLowerCase()} {format(parseISO(cancelDialog.start_date), 'd MMM', { locale: nl })}
              {cancelDialog.start_date !== cancelDialog.end_date && ` t/m ${format(parseISO(cancelDialog.end_date), 'd MMM', { locale: nl })}`}
              {cancelDialog.total_hours != null && ` (${uren(cancelDialog.total_hours)})`}.
              De verlofdagen worden verwijderd en de uren komen terug in het saldo. De medewerker ontvangt een e-mail met de reden.
            </p>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Bijv. op verzoek van de medewerker, vakantie verschoven"
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
              autoFocus
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setCancelDialog(null)}
                className="flex-1 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Terug
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim() || isProcessing === cancelDialog.id}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {isProcessing === cancelDialog.id ? 'Bezig...' : 'Verlof annuleren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
